import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import PropTypes from 'prop-types';
import {makeStyles} from "@material-ui/styles";
import {useDrag, useDrop} from "react-dnd";
import {AcceptTypes, SourceTypes} from "../../../ToolBar/ToolBarUtils/ItemTypes";
import {ScreenDesignerContextNew} from "../../../../../../../../ContextComponents/ScreenDesignerContextNew";
import {getEmptyImage} from "react-dnd-html5-backend";
import {useCurrentResolution} from "../../../../../../../../Hooks/useCurrentResolution";
import {getResponsiveStyles} from "../../../utils/lib";
import {getComponentStyles} from "./getComponentStyles";
import {RenderByType} from "./RenderByType";
import DropArea from "./DropArea";
import {ContextMenu} from "./ContextMenu";
import {DragHandle} from "./DragHandle";

const useStyles = makeStyles((theme) => ({
    componentWrapper: props => ({
        padding: 1,
        border: `1px dashed transparent`,
        //width: '100%',
        position: 'relative',
        '&:hover': {
            border:
                props.isSelected ? `1px dashed ${theme.palette.primary.main} !important` :
                    props.type === 'panel' ? `1px dashed transparent !important` :
                        `1px dashed #cccccc !important`,
        }
    }),
    rowRenderDirection: {
        transition: 'width 0.3s ease',
    },
    columnRenderDirection: {
        transition: 'height 0.3s ease',
    }
}));
const initialContextState = {
    mouseX: null,
    mouseY: null,
};

function propsAreEqual(prevProps, nextProps) {
    return JSON.stringify(prevProps) === JSON.stringify(nextProps)
}
const MemoizedTypedRender = React.memo(RenderByType, propsAreEqual)

const MemoizedDropArea = React.memo(DropArea, (prev, next) => {
    return prev.direction === next.direction && prev.isOverCurrent === next.isOverCurrent
})

const RenderComponents = ({type, item, path, index, parentPath, parentLength, acceptType, renderDirection}) => {

    const {
        onDropComponent,
        onDeleteComponent,
        onSelectComponent,
        onMoveComponent,
        onDuplicateComponent,
        onDropMultipleComponents,
        selectedComponent,
        currentCustomResolution
    } = useContext(ScreenDesignerContextNew);

    const [direction, setDirection] = useState(null);
    const [contextMenu, setContextMenu] = useState(initialContextState);
    const [isVisibleDragger, setIsVisibleDragger] = useState(false);
    const ref = useRef(null);
    const currentResolution = useCurrentResolution();

    const [{isOverCurrent}, drop] = useDrop({
        accept: AcceptTypes[acceptType],
        collect: monitor => ({
            isOverCurrent: monitor.isOver({shallow: true}),
        }),
        hover: (item, monitor) => {
            if (!ref.current) {
                setDir(null)
                return
            }
            if (monitor.didDrop()) {
                setDir(null);
                return
            }
            if(!monitor.isOver({shallow: true})) {
                setDir(null);
                return
            }
            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const clientOffset = monitor.getClientOffset();

            const position = {
                row: {
                    middle: (hoverBoundingRect.right - hoverBoundingRect.left) / 2,
                    client: clientOffset.x - hoverBoundingRect.left
                },
                column: {
                    middle: (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2,
                    client: clientOffset.y - hoverBoundingRect.top,
                }
            };

            if (position[renderDirection].client < position[renderDirection].middle) {
                setDir('top');
                return
            }
            if (position[renderDirection].client > position[renderDirection].middle) {
                setDir('bottom')
            }
        },
        drop: (item, monitor) => {
            const pathToDrop = parentPath ? `${parentPath}.children` : 'children';
            if (monitor.didDrop()) {
                return
            }
            const leftRightDirection = direction === 'top' ? index : index + 1;
            if (item.dragType === 'move') {

                //insert panel inside container
                if (item.component.type === 'panel') {
                    item.component = {
                        ...item.component, children: [{
                            type: 'panel',
                            styles: [{
                                width: "100%",
                                justifyContent: 'flex-start',
                                flexDirection: 'column',
                                alignItems: 'flex-start'
                            }],
                            children: item.component.children
                        }]
                    };
                }

                onMoveComponent(item.component, item.from, pathToDrop, leftRightDirection, item.index, index);
                return
            }
            if (item.dragType === 'multiple') {
                onDropMultipleComponents(item.component, leftRightDirection, pathToDrop);
                return
            }

            //if panel without children, add new fill panel
            if (item.type === 'panel' && item.children && !item.children.length) {
                let newItem = {
                    ...item, children: [{
                        type: 'panel',
                        styles: [{
                            width: "100%",
                            minHeight: "50px",
                            justifyContent: 'flex-start',
                            flexDirection: 'column',
                            alignItems: 'flex-start'
                        }],
                        children: []
                    }]
                };
                onDropComponent(newItem, leftRightDirection, pathToDrop);
                return
            }

            onDropComponent(item, leftRightDirection, pathToDrop)

        }
    });
    const [{isDragging}, drag, preview] = useDrag({
        item: {
            component: {...item, type: SourceTypes[type]},
            type: SourceTypes[type] ? SourceTypes[type] : '',
            dragType: 'move',
            from: path,
            index
        },
        canDrag: true,
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        })
    });

    const isSelected = selectedComponent && selectedComponent.path === path;
    const classes = useStyles({isSelected, type: item.type});

    const styles = useMemo(() => {
      return getResponsiveStyles(item.styles, currentCustomResolution);
    }, [JSON.stringify(item.styles), currentCustomResolution])

    const stylesLength = item.styles ? item.styles.length : 0;
    drop(ref);

    useEffect(() => {
        preview(getEmptyImage(), {
            captureDraggingState: true
        })
    }, [preview]);

    //set direction where to show drop-area (bottom, top, right, left)
    const setDir = dir => {
        if(dir !== direction) setDirection(dir);
    };

    const openContextMenu = (e, item, path) => {

        //disable context menu for grid container
        if (item.type === 'panel') return;

        if (contextMenu.mouseY) {
            setContextMenu(initialContextState);
            return
        }
        e.stopPropagation();
        e.preventDefault();
        onSelectComponent(item, path, ref);
        setContextMenu({
            mouseX: e.clientX - 2,
            mouseY: e.clientY - 4,
        })
    };
    const closeContextMenu = () => {
        setContextMenu(initialContextState)
    };

    const {
        height,
        width,
        maxWidth,
        margin,
        padding,
        background,
        border,
        position,
        left,
        top
    } = useMemo(() => {
      return getComponentStyles(styles, stylesLength, isSelected, type, currentResolution, item.children);
    }, [JSON.stringify(styles), stylesLength, isSelected, type, currentResolution, JSON.stringify(item.children)])

    if (!item.type) return <div>Broken component</div>;
    return (
        <div
            className={`${classes.componentWrapper}`}
            ref={ref}
            onMouseEnter={() => setIsVisibleDragger(item.type !== 'panel')}
            onMouseLeave={() => setIsVisibleDragger(false)}
            onContextMenu={(e) => openContextMenu(e, item, path)}
            style={{
                opacity: isDragging ? 0.5 : 1,
                minHeight: height,
                height: type === 'list' ? height : height,
                width,
                maxWidth,
                margin,
                padding,
                background,
                border,
                position,
                top,
                left
            }}
            onClick={(e) => {
                e.stopPropagation();
                //disable context menu for grid container
                if (item.type === 'panel') return;
                onSelectComponent(item, path, ref)
            }}
        >

            <MemoizedTypedRender
                type={type}
                component={item}
                path={path}
                index={index}
                parentPath={parentPath}
            />

            {/*drop area*/}
            {!isDragging && (
                <MemoizedDropArea
                    direction={direction}
                    isOverCurrent={isOverCurrent}
                    renderDirection={renderDirection}/>
            )}

            {/*drag handler*/}
            <div
                ref={drag}
                style={{display: isVisibleDragger ? 'block' : 'none'}}
            >
                <DragHandle type={type}/>
            </div>

            {/*context menu*/}
            <ContextMenu
                contextMenu={contextMenu}
                onClose={closeContextMenu}
                item={item}
                removeItem={onDeleteComponent}
                editItem={onSelectComponent}
                duplicateItem={onDuplicateComponent}
                onDropComponent={onDropComponent}
                parentPath={parentPath}
                parentLength={parentLength}
                index={index}
                path={path}
            />
        </div>
    )
};

RenderComponents.propTypes = {
    type: PropTypes.string,
    item: PropTypes.object,
    index: PropTypes.number,
    path: PropTypes.string,
    parentPath: PropTypes.string,
    acceptType: PropTypes.string, //define custom drop acceptType, by default value is 'componentTarget'
    renderDirection: PropTypes.oneOf(['row', 'column']) //drop direction, to show drop area(top-bottom / left-right)
};

RenderComponents.defaultProps = {
    acceptType: 'componentTarget',
    renderDirection: 'column'
};

export default RenderComponents;